// This file includes the mapping between the route and the component that should be rendered for that route.
// You are not meant to import this file on client-side code, as it will make the bundle size bigger.

import {
	CustomerLandingPageRelayQuery,
	getCustomerLandingPageRelayQueryVars,
} from 'src/contentful/graphql/customer-landing-page';

import { RELAY_API_CONTENTFUL } from '@/utils/constant';
import { experiments } from '@/utils/experiment/manifest';
import { getExperiment, getExperimentWithExposures } from '@/utils/experiment/use-experiment';
import { isProduction } from '@/utils/urls.ts';

import {
	AnchorPageRelayQuery,
	getAnchorPageRelayQueryVars,
} from './contentful/graphql/anchor-page';
import {
	getPricingPageRelayQueryVars,
	PricingPageRelayQuery,
} from './contentful/graphql/pricing-page';
import { FourOhFourPage } from './pages/404/404-page';
import { BuyCloudOrgSetupPage } from './pages/buy/cloud/org-setup/buy-cloud-org-setup-page';
import { BuyCloudPostOrderProvisioningPage } from './pages/buy/cloud/post-order-provisioning/buy-cloud-post-order-provisioning-page';
import { BuyCloudSiteSetupPage } from './pages/buy/cloud/site-setup/buy-cloud-site-setup-page.tsx';
import CDNLibrary from './pages/cdn-library/cdn-library.tsx';
import { CustomersLandingPage } from './pages/customers/customers-landing-page';
import { EnterpriseContactSalesPage } from './pages/enterprise/contact/enterprise-contact-sales-page.tsx';
import { EnterpriseContactThanksPage } from './pages/enterprise/contact-thanks/enterprise-contact-thanks-page';
import { CheckemailPage } from './pages/ondemand/signup/checkemail/checkemail-page';
import { ConfirmationPage } from './pages/ondemand/signup/confirmation/confirmation-page';
import { SearchResultsPage } from './pages/search/search.tsx';
import { SoftwareBitbucketPricingPage } from './pages/software/bitbucket/pricing/software-bitbucket-pricing-page';
import { ConfluenceSoftwarePageAnchorAutomation } from './pages/software/confluence/anchors/anchor-lp-conf-automation';
import { ConfluenceSoftwarePageVariantAnchorLpConfCollaboration } from './pages/software/confluence/anchors/anchor-lp-conf-collaboration';
import { ConfluenceSoftwarePageAnchorCompetitors } from './pages/software/confluence/anchors/anchor-lp-conf-competitors';
import { ConfluenceSoftwarePageAnchorCustomerService } from './pages/software/confluence/anchors/anchor-lp-conf-team-customerservice.tsx';
import { ConfluenceSoftwarePageAnchorDesign } from './pages/software/confluence/anchors/anchor-lp-conf-team-design.tsx';
import { ConfluenceSoftwarePageAnchorEngineering } from './pages/software/confluence/anchors/anchor-lp-conf-team-engineering';
import { ConfluenceSoftwarePageAnchorHr } from './pages/software/confluence/anchors/anchor-lp-conf-team-hr.tsx';
import { ConfluenceSoftwarePageAnchorLegal } from './pages/software/confluence/anchors/anchor-lp-conf-team-legal.tsx';
import { ConfluenceSoftwarePageAnchorMarketing } from './pages/software/confluence/anchors/anchor-lp-conf-team-marketing.tsx';
import { ConfluenceSoftwarePageAnchorOperations } from './pages/software/confluence/anchors/anchor-lp-conf-team-operations.tsx';
import { ConfluenceSoftwarePageAnchorProgramMgmt } from './pages/software/confluence/anchors/anchor-lp-conf-team-programmgmt.tsx';
import { ConfluenceSoftwarePageAnchorTeamwork } from './pages/software/confluence/anchors/anchor-lp-conf-teamwork';
import { ConfluenceSoftwarePageAnchorWorkManagement } from './pages/software/confluence/anchors/anchor-lp-conf-work-management';
import { ConfluenceSoftwarePage } from './pages/software/confluence/anchors/confluence-software-page';
import {
	isTargetedKeyword,
	getPageId as getPageIdKeywords,
} from './pages/software/confluence/anchors/keywords/keyword-util.tsx';
import { ConfluenceSoftwarePageVariantPaidLpConfFeatures } from './pages/software/confluence/anchors/paid-lp-conf-features';
import { ConfluenceSoftwarePageVariantPaidLpConfKeywords } from './pages/software/confluence/anchors/paid-lp-conf-keywords.tsx';
import { ConfluenceSoftwarePageVariantPaidLpConfSubAnchors } from './pages/software/confluence/anchors/paid-lp-conf-sub-anchors.tsx';
import { ConfluenceSoftwarePageVariantPaidLpConflDocumentation } from './pages/software/confluence/anchors/paid-lp-confl-documentation';
import { ConfluenceSoftwarePageVariantPaidLpConflTemplate } from './pages/software/confluence/anchors/paid-lp-confl-template';
import {
	isTargetedSubAnchor,
	isInContextualBanditsSubAnchor,
	getPageId,
	isInConfluenceCBSubAnchorCMSVariant,
	isInConfluenceMigratedAnchors,
} from './pages/software/confluence/anchors/subAnchors/subAnchor-util.tsx';
import { ConfluenceFreePage } from './pages/software/confluence/free/confluence-free-page.tsx';
import { ConfluencePricingPage } from './pages/software/confluence/pricing/confluence-pricing-page';
import { getPricingTitleInternal as getConfluencePricingTitleInternal } from './pages/software/confluence/pricing/feature-gates';
import { ConfluenceTemplateCategoryRelayPage as ConfluenceTemplateCategoryContentfulRelayPage } from './pages/software/confluence/templates/categories/confluence-template-category-contentful-relay-page';
import { ConfluenceTemplateCollectionDetailsRelayPage as ConfluenceTemplateCollectionDetailsContentfulRelayPage } from './pages/software/confluence/templates/collections/confluence-template-collection-details-contentful-relay-page';
import { ConfluenceTemplatesRelayPage as ConfluenceTemplatesContentfulRelayPage } from './pages/software/confluence/templates/confluence-templates-contentful-relay-page';
import { ConfluenceTemplateSearchRelayPage as ConfluenceTemplateSearchContentfulRelayPage } from './pages/software/confluence/templates/search/confluence-template-search-contentful-relay-page';
import { JiraSoftwarePageAnchorLpJswDesign } from './pages/software/jira/anchors/anchor-lp-jsw-team-design.tsx';
import { JiraSoftwarePageAnchorLpJswEngineering } from './pages/software/jira/anchors/anchor-lp-jsw-team-engineering.tsx';
import { JiraSoftwarePageAnchorLpJswHr } from './pages/software/jira/anchors/anchor-lp-jsw-team-hr.tsx';
import { JiraSoftwarePageAnchorLpJswLegal } from './pages/software/jira/anchors/anchor-lp-jsw-team-legal.tsx';
import { JiraSoftwarePageAnchorLpJswMarketing } from './pages/software/jira/anchors/anchor-lp-jsw-team-marketing.tsx';
import { JiraSoftwarePageAnchorLpJswOperations } from './pages/software/jira/anchors/anchor-lp-jsw-team-operations.tsx';
import { JiraSoftwarePageAnchorLpJswProgramMgmt } from './pages/software/jira/anchors/anchor-lp-jsw-team-programmgmt.tsx';
import { JiraSoftwarePageAnchorLpJswSales } from './pages/software/jira/anchors/anchor-lp-jsw-team-sales.tsx';
import { jiraMigratedAnchorVariants } from './pages/software/jira/anchors/jira-migrated-anchor-variants';
import { JiraSoftwarePage } from './pages/software/jira/anchors/jira-software-page';
import { JiraSoftwarePagePaidLpJswAgile } from './pages/software/jira/anchors/paid-lp-jsw-agile.tsx';
import { JiraSoftwarePagePaidLpJswAutomation } from './pages/software/jira/anchors/paid-lp-jsw-automation.tsx';
import { JiraSoftwarePagePaidLpJswCompetitors } from './pages/software/jira/anchors/paid-lp-jsw-competitors.tsx';
import { JiraSoftwarePagePaidLpJswFeatures } from './pages/software/jira/anchors/paid-lp-jsw-features';
import { JiraSoftwarePagePaidLpJswPm } from './pages/software/jira/anchors/paid-lp-jsw-pm';
import { JiraSoftwarePagePaidLpJswProjCollab } from './pages/software/jira/anchors/paid-lp-jsw-project-collaboration.tsx';
import { JiraSoftwarePagePaidLpJswRoadmaps } from './pages/software/jira/anchors/paid-lp-jsw-roadmaps';
import { JiraSoftwarePagePaidLpJswTeamwork } from './pages/software/jira/anchors/paid-lp-jsw-teamwork.tsx';
import { JiraSoftwarePagePaidLpJswTemplate } from './pages/software/jira/anchors/paid-lp-jsw-template';
import { JiraSoftwarePagePaidLpJswTracker } from './pages/software/jira/anchors/paid-lp-jsw-tracker';
import { JiraSoftwareFeaturesPage } from './pages/software/jira/features/jira-software-features-page';
import { JiraSoftwareFreePage } from './pages/software/jira/free/jira-software-free-page';
import { getPricingTitleInternal as getJiraPricingTitleInternal } from './pages/software/jira/pricing/feature-gates';
import { JiraSoftwarePricingPage } from './pages/software/jira/pricing/jira-software-pricing-page';
import { JiraProductDiscoveryPage } from './pages/software/jira/product-discovery/jira-product-discovery-page';
import { JiraProductDiscoveryPricingPage } from './pages/software/jira/product-discovery/pricing/jira-product-discovery-pricing-page.tsx';
import { getPricingTitleInternal as getJiraServiceManagementPricingTitleInternal } from './pages/software/jira/service-management/pricing/feature-gates';
import { JiraServiceManagementPricingPage } from './pages/software/jira/service-management/pricing/jira-service-management-pricing-page';
import { JiraTemplateCategoryPage } from './pages/software/jira/templates/service-management/jira-template-category-page';
import { LoomPricingPage } from './pages/software/loom/pricing/loom-pricing-page.tsx';
import { SoftwareRovoPricingPage } from './pages/software/rovo/pricing/software-rovo-pricing-page';
import { UsageBasedPricingPage } from './pages/software/rovo/pricing/usage-based-pricing/usage-based-pricing-page';
import { SoftwareRovoStartPage } from './pages/software/rovo/start/software-rovo-start-page';
import { NonProfitPricingPage } from './pages/teams/nonprofit/pricing/non-profit-pricing-page';
import { LoomChoicePage } from './pages/try/cloud/loom/choose/loom-choice-page.tsx';
import { SignupPage } from './pages/try/cloud/signup/signup-page';
import { TryPage } from './pages/try/try-page';
import { UniformConfluencePricingEntryPage } from './pages/uniform-entry/uniform-confluence-pricing-entry-page.tsx';
import { UniformEntryPage } from './pages/uniform-entry/uniform-entry-page';
import { UniformJiraPricingEntryPage } from './pages/uniform-entry/uniform-jira-pricing-entry-page.tsx';
import { UniformPreviewPage } from './pages/uniform-preview/uniform-preview-page';
import {
	ConfluenceTemplateCollectionDetailsRelayPageQuery as ConfluenceTemplateCollectionDetailsContentfulRelayPageQuery,
	getConfluenceTemplateCollectionDetailsRelayPageQueryVars as getConfluenceTemplateCollectionDetailsContentfulRelayPageQueryVars,
} from './templates/software/confluence/templates/confluence-templates-page/src/relay/contentful-relay/confluence-template-collection-details-contentful-relay-page.template.tsx';
import {
	ConfluenceTemplatesUnifiedRelayPageQuery as ConfluenceTemplatesUnifiedContentfulRelayPageQuery,
	getConfluenceTemplatesUnifiedRelayPageQueryVars as getConfluenceTemplatesUnifiedContentfulRelayPageQueryVars,
} from './templates/software/confluence/templates/confluence-templates-page/src/relay/contentful-relay/confluence-templates-unified-contentful-relay-page.template';
import { isInternalWacWebHostname } from './uniform/preview-access';
import { inRoutingTable } from './utils/dynamic-routing/dynamic-routing-utils';
import { checkFeatureFlagGate } from './utils/feature-flags/check-feature-flag.ts';
import { featureFlags } from './utils/feature-flags/feature-flag-manifest.ts';
import { getRegionFromCountryCode } from './utils/useGeoCurrency';
import { isVanityHostname, getVanitySegment } from './utils/vanity-hostname.ts';
import { parseRoute } from './wac-routes';

export const getRoute = (
	location: Location,
	userLocale?: string,
	userLoggedIn?: boolean,
	subAnchor?: string,
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	routingTable?: any,
	isEdgeRoutedToWae = false,
	// eslint-disable-next-line complexity
) => {
	const route = parseRoute(location);

	// Helper function to prepare segments for routing table lookup
	const prepareRoutingSegments = (routePath: string, hostname: string) => {
		const vanitySegment = getVanitySegment(hostname);
		if (vanitySegment) {
			const actualPath = window.location.pathname;
			if (actualPath === '/' || actualPath === '') {
				return [vanitySegment]; // Homepage
			} else {
				const pathSegments = actualPath.split('/').filter((segment) => segment !== '');
				// /product is stripped from bitbucket.org paths before
				// they reach any backend. The Uniform project map mirrors
				// Magnolia's content tree which nests pages under
				// bitbucketapp/product/*, so we re-insert the segment here
				// (same approach Magnolia uses via prependProductToAllBitbucketUrls).
				// https://bitbucket.org/atlassian/magnolia/src/8f7194d2cd01a1c89444c0b626b6e19ecd92a61e/atlassian-configuration/src/main/java/com/atlassian/magnolia/custom/templating/functions/CustomAtlassianTemplatingFunctions.java?at=master#CustomAtlassianTemplatingFunctions.java-80,84,86,96,141
				if (vanitySegment === 'bitbucketapp' && pathSegments[0] !== 'product') {
					return [vanitySegment, 'product', ...pathSegments];
				}
				return [vanitySegment, ...pathSegments];
			}
		} else {
			// bxp-nginx maps vanity domains (e.g. bitbucket.org)
			// to path-prefixed routes (e.g. bitbucketapp/write) on an internal
			// hostname, so getVanitySegment() returns null. We still need to
			// re-insert /product for bitbucketapp paths since nginx strips it.
			const segments = routePath.split('/');
			if (segments[0] === 'bitbucketapp' && segments[1] !== 'product') {
				return [segments[0], 'product', ...segments.slice(1)];
			}
			return segments;
		}
	};

	// check if query params contains compositionSource
	const compositionSource = new URLSearchParams(location.search).get('compositionSource');
	if (compositionSource === 'live' && process.env['UNIFORM_PREVIEW_SECRET']) {
		return {
			Component: UniformEntryPage,
			entryName: 'uniform-entry-page',
			isS3UniformRoute: true,
		};
	}

	if (isEdgeRoutedToWae) {
		const segments = prepareRoutingSegments(route.path, location.hostname);
		return {
			Component: UniformEntryPage,
			entryName: 'uniform-entry-page',
			isEdgeRoutedToWae: true,
			isUniformCanary: route.isUniformCanary,
			isS3UniformRoute: true,
			resolvedContentPath: segments.join('/'),
		};
	}

	// Check if page is a Uniform-supported route
	const checkRoutingTable = checkFeatureFlagGate(featureFlags.routingTable);
	if (checkRoutingTable && routingTable) {
		const segments = prepareRoutingSegments(route.path, location.hostname);
		const segLength = segments.length;
		if (
			routingTable &&
			(route.path !== '' || isVanityHostname(location.hostname)) &&
			inRoutingTable(segments, 0, segLength - 1, routingTable['wac'])
		) {
			console.log(
				route.path,
				'is Uniform route served by Routing Table (resolved:',
				segments.join('/'),
				')',
			);
			return {
				Component: UniformEntryPage,
				entryName: 'uniform-entry-page',
				isUniformCanary: route.isUniformCanary,
				isS3UniformRoute: true,
				resolvedContentPath: segments.join('/'),
			};
		} else {
			console.log(route.path, 'is NOT Uniform route');
		}
	}

	switch (route.path) {
		// todo: gwp-11369 delete after feature is tested, merged, and deployed
		case `test-gql-pricing/${getRouteExtension(route.path, 'test-gql-pricing/')}`: {
			if (!isProduction()) {
				return {
					Component: UniformEntryPage,
					entryName: 'uniform-entry-page',
					isUniformCanary: route.isUniformCanary,
					isS3UniformRoute: true,
				};
			}
			return { Component: FourOhFourPage, entryName: '404' };
		}
		case 'wae-inventory':
		case `wae-inventory/${getRouteExtension(route.path, 'wae-inventory')}`:
		case 'banners':
			if (!isProduction()) {
				return {
					Component: UniformEntryPage,
					entryName: 'uniform-entry-page',
					isUniformCanary: route.isUniformCanary,
					isS3UniformRoute: true,
				};
			}
			return { Component: FourOhFourPage, entryName: '404' };
		case 'uniform-preview': {
			// Two independent gates decide whether this route resolves at all.
			//
			// UNIFORM_PREVIEW_SECRET is set only in non-production environments
			// (staging, dev, local), so its presence means "this is a preview
			// environment". NODE_ENV cannot serve that purpose: Parcel/Atlaspack
			// bakes it in at build time and it is "production" for staging builds
			// too, which would incorrectly block the route on staging.
			//
			// UNIFORM_PREVIEW_ENABLED opts an environment into serving the route
			// even when it is not a preview environment — production, in practice.
			// It is deliberately a non-secret boolean: this module runs during
			// client hydration as well as SSR, and every env var read here is
			// inlined into the browser bundle by the bundler.
			//
			// This gate only decides whether the route resolves. What the page is
			// allowed to *render* — the projectId allow-list — lives in
			// resolve-page-props.ts, which is server-only and so is the right place
			// for it. Both must agree, or prod serves a route that renders nothing.
			const isPreviewEnvironment = Boolean(process.env['UNIFORM_PREVIEW_SECRET']);
			const isPreviewRouteEnabled = process.env['UNIFORM_PREVIEW_ENABLED'] === 'true';

			if (!isPreviewEnvironment && !isPreviewRouteEnabled) {
				return { Component: FourOhFourPage, entryName: '404' };
			}

			// Outside a preview environment the route is served only on wac-web's
			// own Bifrost hostname, never on a customer-facing domain such as
			// www.atlassian.com. The migration tooling calls that host directly, so
			// this costs it nothing.
			//
			// Note this keeps the route off the marketing domain; it does not make
			// it internal-only. prod-east does not set privateAccessOnly, so the
			// Bifrost hostname is publicly reachable. The projectId allow-list and
			// the compositionId requirement are what actually limit what renders.
			if (!isPreviewEnvironment && !isInternalWacWebHostname(location.hostname)) {
				return { Component: FourOhFourPage, entryName: '404' };
			}

			switch (route.variant) {
				default:
					return {
						Component: UniformPreviewPage,
						entryName: 'uniform-preview-page',
					};
			}
		}

		// this route name is temporary and not confirmed yet, but using for development purposes
		case 'resources/usage/calculator':
			if (!checkFeatureFlagGate(featureFlags.gwp11118UsageBasedPricingCalculator)) {
				return {
					Component: FourOhFourPage,
					entryName: '404',
				};
			}
			return {
				Component: UsageBasedPricingPage,
				entryName: 'usage-based-pricing-page',
			};
		case 'software/rovo/pricing':
			switch (route.variant) {
				default:
					return {
						Component: SoftwareRovoPricingPage,
						entryName: 'software-rovo-pricing-page',
						query: PricingPageRelayQuery,
						queryVars: getPricingPageRelayQueryVars('rovo', location),
						isRelayEnabled: true,
						relayApi: RELAY_API_CONTENTFUL,
					};
			}
		case 'software/jira/product-discovery/pricing':
			switch (route.variant) {
				default:
					const isWae = checkFeatureFlagGate(featureFlags.gwp7856PricingPagesWaeMigrationM3);
					if (isWae) {
						return {
							Component: UniformEntryPage,
							entryName: 'uniform-entry-page',
							isUniformCanary: route.isUniformCanary,
							isS3UniformRoute: true,
						};
					}

					return {
						Component: JiraProductDiscoveryPricingPage,
						entryName: 'jira-product-discovery-pricing-page',
						query: PricingPageRelayQuery,
						queryVars: getPricingPageRelayQueryVars(
							'jira-product-discovery',
							location,
							'Jira Product Discovery Pricing Page',
						),
						isRelayEnabled: true,
						relayApi: RELAY_API_CONTENTFUL,
					};
			}
		case 'software/loom/pricing':
			switch (route.variant) {
				default:
					const isWae = checkFeatureFlagGate(featureFlags.gwp7856PricingPagesWaeMigrationM3);
					if (isWae) {
						return {
							Component: UniformEntryPage,
							entryName: 'uniform-entry-page',
							isUniformCanary: route.isUniformCanary,
							isS3UniformRoute: true,
						};
					}
					return {
						Component: LoomPricingPage,
						entryName: 'loom-pricing-page',
						query: PricingPageRelayQuery,
						queryVars: getPricingPageRelayQueryVars('loom', location),
						isRelayEnabled: true,
						relayApi: RELAY_API_CONTENTFUL,
					};
			}
		case `software/loom${getRouteExtension(route.path, 'software/loom')}`:
			return {
				Component: UniformEntryPage,
				entryName: 'uniform-entry-page',
				isUniformCanary: route.isUniformCanary,
				isS3UniformRoute: true,
			};
		case 'buy/cloud/org-setup':
			switch (route.variant) {
				default:
					return {
						Component: BuyCloudOrgSetupPage,
						entryName: 'buy-cloud-org-setup-page',
					};
			}
		case 'buy/cloud/site-setup':
			switch (route.variant) {
				default:
					return {
						Component: BuyCloudSiteSetupPage,
						entryName: 'buy-cloud-site-setup-page',
					};
			}
		case 'software/bitbucket/pricing':
			switch (route.variant) {
				default:
					const isWae = checkFeatureFlagGate(featureFlags.gwp7856PricingPagesWaeMigrationM3);
					if (isWae) {
						return {
							Component: UniformEntryPage,
							entryName: 'uniform-entry-page',
							isUniformCanary: route.isUniformCanary,
							isS3UniformRoute: true,
						};
					}
					return {
						Component: SoftwareBitbucketPricingPage,
						entryName: 'software-bitbucket-pricing-page',
						query: PricingPageRelayQuery,
						queryVars: getPricingPageRelayQueryVars('bitbucket', location),
						isRelayEnabled: true,
						relayApi: RELAY_API_CONTENTFUL,
					};
			}
		case 'buy/cloud/post-order-provisioning':
			switch (route.variant) {
				default:
					return {
						Component: BuyCloudPostOrderProvisioningPage,
						entryName: 'buy-cloud-post-order-provisioning-page',
					};
			}
		case 'software/rovo/start':
			switch (route.variant) {
				default:
					return {
						Component: SoftwareRovoStartPage,
						entryName: 'software-rovo-start-page',
					};
			}
		case 'customers':
			return {
				Component: CustomersLandingPage,
				entryName: 'customers-landing-page',
				query: CustomerLandingPageRelayQuery,
				queryVars: getCustomerLandingPageRelayQueryVars(location),
				isRelayEnabled: true,
				relayApi: RELAY_API_CONTENTFUL,
			};
		case 'software/jira':
			const countryCode = userLocale?.trim().toUpperCase();
			if (
				userLoggedIn === false &&
				jiraMigratedAnchorVariants.includes(route.variant) &&
				getRegionFromCountryCode(countryCode) !== 'unknown' &&
				!['AU', 'NZ', 'GB', 'DE'].includes(countryCode ?? '')
			) {
				const jiraWaeExperiment = getExperimentWithExposures(
					experiments.gwpJiraAnchorJourneyWaeMigration,
				);

				if (jiraWaeExperiment.type === 'enrolled' && jiraWaeExperiment.value === true) {
					return {
						Component: UniformEntryPage,
						entryName: 'uniform-entry-page',
						isUniformCanary: route.isUniformCanary,
						isS3UniformRoute: true,
					};
				}
			}

			switch (route.variant) {
				case 'paid-lp-jsw-features':
					return {
						Component: JiraSoftwarePagePaidLpJswFeatures,
						entryName: 'jira-software-page-paid-lp-jsw-features',
					};
				case 'paid-lp-jsw-roadmaps':
					return {
						Component: JiraSoftwarePagePaidLpJswRoadmaps,
						entryName: 'jira-software-page-paid-lp-jsw-roadmaps',
						query: AnchorPageRelayQuery,
						queryVars: getAnchorPageRelayQueryVars(route.variant, location),
						isRelayEnabled: true,
						relayApi: RELAY_API_CONTENTFUL,
					};
				case 'paid-lp-jsw-template':
					return {
						Component: JiraSoftwarePagePaidLpJswTemplate,
						entryName: 'jira-software-page-paid-lp-jsw-template',
						query: AnchorPageRelayQuery,
						queryVars: getAnchorPageRelayQueryVars(route.variant, location),
						isRelayEnabled: true,
						relayApi: RELAY_API_CONTENTFUL,
					};
				case 'paid-lp-jsw-tracker':
					return {
						Component: JiraSoftwarePagePaidLpJswTracker,
						entryName: 'jira-software-page-paid-lp-jsw-tracker',
						query: AnchorPageRelayQuery,
						queryVars: getAnchorPageRelayQueryVars(route.variant, location),
						isRelayEnabled: true,
						relayApi: RELAY_API_CONTENTFUL,
					};
				case 'paid-lp-jsw-pm':
					return {
						Component: JiraSoftwarePagePaidLpJswPm,
						entryName: 'jira-software-page-paid-lp-jsw-pm',
						query: AnchorPageRelayQuery,
						queryVars: getAnchorPageRelayQueryVars(route.variant, location),
						isRelayEnabled: true,
						relayApi: RELAY_API_CONTENTFUL,
					};
				case 'paid-lp-jsw-project_collaboration':
					return {
						Component: JiraSoftwarePagePaidLpJswProjCollab,
						entryName: 'jira-software-page-paid-lp-jsw-project_collaboration',
						query: AnchorPageRelayQuery,
						queryVars: getAnchorPageRelayQueryVars(route.variant, location),
						isRelayEnabled: true,
						relayApi: RELAY_API_CONTENTFUL,
					};
				case 'paid-lp-jsw-automation':
					return {
						Component: JiraSoftwarePagePaidLpJswAutomation,
						entryName: 'jira-software-page-paid-lp-jsw-automation',
						query: AnchorPageRelayQuery,
						queryVars: getAnchorPageRelayQueryVars(route.variant, location),
						isRelayEnabled: true,
						relayApi: RELAY_API_CONTENTFUL,
					};
				case 'paid-lp-jsw-teamwork':
					return {
						Component: JiraSoftwarePagePaidLpJswTeamwork,
						entryName: 'jira-software-page-paid-lp-jsw-teamwork',
						query: AnchorPageRelayQuery,
						queryVars: getAnchorPageRelayQueryVars(route.variant, location),
						isRelayEnabled: true,
						relayApi: RELAY_API_CONTENTFUL,
					};
				case 'paid-lp-jsw-agile':
					return {
						Component: JiraSoftwarePagePaidLpJswAgile,
						entryName: 'jira-software-page-paid-lp-jsw-agile',
						query: AnchorPageRelayQuery,
						queryVars: getAnchorPageRelayQueryVars(route.variant, location),
						isRelayEnabled: true,
						relayApi: RELAY_API_CONTENTFUL,
					};
				case 'paid-lp-jsw-competitors':
					return {
						Component: JiraSoftwarePagePaidLpJswCompetitors,
						entryName: 'jira-software-page-paid-lp-jsw-competitors',
						query: AnchorPageRelayQuery,
						queryVars: getAnchorPageRelayQueryVars(route.variant, location),
						isRelayEnabled: true,
						relayApi: RELAY_API_CONTENTFUL,
					};
				case 'anchor-lp-jsw-team-engineering':
					return {
						Component: JiraSoftwarePageAnchorLpJswEngineering,
						entryName: 'jira-software-page-anchor-lp-jsw-team-engineering',
						query: AnchorPageRelayQuery,
						queryVars: getAnchorPageRelayQueryVars(route.variant, location),
						isRelayEnabled: true,
						relayApi: RELAY_API_CONTENTFUL,
					};
				case 'anchor-lp-jsw-team-marketing':
					return {
						Component: JiraSoftwarePageAnchorLpJswMarketing,
						entryName: 'jira-software-page-anchor-lp-jsw-team-marketing',
						query: AnchorPageRelayQuery,
						queryVars: getAnchorPageRelayQueryVars(route.variant, location),
						isRelayEnabled: true,
						relayApi: RELAY_API_CONTENTFUL,
					};
				case 'anchor-lp-jsw-team-hr':
					return {
						Component: JiraSoftwarePageAnchorLpJswHr,
						entryName: 'jira-software-page-anchor-lp-jsw-team-hr',
						query: AnchorPageRelayQuery,
						queryVars: getAnchorPageRelayQueryVars(route.variant, location),
						isRelayEnabled: true,
						relayApi: RELAY_API_CONTENTFUL,
					};
				case 'anchor-lp-jsw-team-legal':
					return {
						Component: JiraSoftwarePageAnchorLpJswLegal,
						entryName: 'jira-software-page-anchor-lp-jsw-team-legal',
						query: AnchorPageRelayQuery,
						queryVars: getAnchorPageRelayQueryVars(route.variant, location),
						isRelayEnabled: true,
						relayApi: RELAY_API_CONTENTFUL,
					};
				case 'anchor-lp-jsw-team-operations':
					return {
						Component: JiraSoftwarePageAnchorLpJswOperations,
						entryName: 'jira-software-page-anchor-lp-jsw-team-operations',
						query: AnchorPageRelayQuery,
						queryVars: getAnchorPageRelayQueryVars(route.variant, location),
						isRelayEnabled: true,
						relayApi: RELAY_API_CONTENTFUL,
					};
				case 'anchor-lp-jsw-team-design':
					return {
						Component: JiraSoftwarePageAnchorLpJswDesign,
						entryName: 'jira-software-page-anchor-lp-jsw-team-design',
						query: AnchorPageRelayQuery,
						queryVars: getAnchorPageRelayQueryVars(route.variant, location),
						isRelayEnabled: true,
						relayApi: RELAY_API_CONTENTFUL,
					};
				case 'anchor-lp-jsw-team-programmgmt':
					return {
						Component: JiraSoftwarePageAnchorLpJswProgramMgmt,
						entryName: 'jira-software-page-anchor-lp-jsw-team-programmgmt',
						query: AnchorPageRelayQuery,
						queryVars: getAnchorPageRelayQueryVars(route.variant, location),
						isRelayEnabled: true,
						relayApi: RELAY_API_CONTENTFUL,
					};
				case 'anchor-lp-jsw-team-sales':
					return {
						Component: JiraSoftwarePageAnchorLpJswSales,
						entryName: 'jira-software-page-anchor-lp-jsw-team-sales',
						query: AnchorPageRelayQuery,
						queryVars: getAnchorPageRelayQueryVars(route.variant, location),
						isRelayEnabled: true,
						relayApi: RELAY_API_CONTENTFUL,
					};
				default:
					//For logged in, we will either show a "Has Jira" experience, otherwise the Uniform "logged out" page
					return {
						Component: JiraSoftwarePage,
						entryName: 'jira-software-page',
						// eslint-disable-next-line @typescript-eslint/no-explicit-any
						isUniformCanary: (route as any).isUniformCanary,
						isS3UniformRoute: true,
					};
			}
		case 'software/jira/product-discovery':
			return {
				Component: JiraProductDiscoveryPage,
				entryName: 'jira-product-discovery-page',
			};
		case 'software/jira/features':
			return {
				Component: JiraSoftwareFeaturesPage,
				entryName: 'jira-software-features-page',
			};

		case `software/jira/guides${getRouteExtension(route.path, 'software/jira/guides')}`: {
			return {
				Component: UniformEntryPage,
				entryName: 'uniform-entry-page',
				isUniformCanary: route.isUniformCanary,
				isS3UniformRoute: true,
			};
		}

		case 'software/jira/free':
			return {
				Component: JiraSoftwareFreePage,
				entryName: 'jira-software-free-page',
			};

		case 'software/jira/pricing':
			const isWae = checkFeatureFlagGate(featureFlags.jiraPricingWaeMigrationExperimentRerunGate);
			const isJiraPricingExperiment = getExperiment(
				experiments.jiraPricingWaeMigrationExperimentRerunExp,
			);

			if (
				isWae &&
				isJiraPricingExperiment.type === 'enrolled' &&
				isJiraPricingExperiment.value === true
			) {
				return {
					Component: UniformJiraPricingEntryPage,
					entryName: 'uniform-jira-pricing-entry-page',
					isUniformCanary: route.isUniformCanary,
					isS3UniformRoute: true,
				};
			}

			const jiraPricingTitleInternal = getJiraPricingTitleInternal();

			return {
				Component: JiraSoftwarePricingPage,
				entryName: 'jira-software-pricing-page',
				query: PricingPageRelayQuery,
				queryVars: getPricingPageRelayQueryVars(
					'jira-software',
					location,
					jiraPricingTitleInternal,
				),
				isRelayEnabled: true,
				relayApi: RELAY_API_CONTENTFUL,
			};

		case 'software/jira/service-management':
			// main product tour page and all anchors are on WAE
			return {
				Component: UniformEntryPage,
				entryName: 'uniform-entry-page',
				isUniformCanary: route.isUniformCanary,
				isS3UniformRoute: true,
			};
		case 'software/confluence':
			const isCBSubAnchorPriority =
				isInContextualBanditsSubAnchor(subAnchor) && route.locale === 'en';

			const excludedCountyCodesFromExperiment = ['IN', 'ID', 'GB'];
			const excludedCountry =
				userLocale !== null &&
				userLocale !== undefined &&
				excludedCountyCodesFromExperiment.includes(userLocale);

			if (route.variant && isTargetedSubAnchor(subAnchor) && !excludedCountry) {
				const subAnchorExperiment = getExperiment(experiments.cfind4782LandingPageSubAnchorV3);
				// if treatment return the sub anchor experiment, else control gets whatever normal route behavior is
				if (subAnchorExperiment.type === 'enrolled' && subAnchorExperiment.value) {
					return {
						Component: ConfluenceSoftwarePageVariantPaidLpConfSubAnchors,
						entryName: 'confluence-software-page-variant-paid-lp-conf-subAnchors',
						query: AnchorPageRelayQuery,
						queryVars: getAnchorPageRelayQueryVars(getPageId(subAnchor), location),
						isRelayEnabled: true,
						relayApi: RELAY_API_CONTENTFUL,
					};
				}
			}

			if (
				!userLoggedIn &&
				route.variant &&
				isTargetedKeyword(route.keyword) &&
				!isCBSubAnchorPriority
			) {
				return {
					Component: ConfluenceSoftwarePageVariantPaidLpConfKeywords,
					entryName: 'confluence-software-page-variant-paid-lp-conf-keywords',
					query: AnchorPageRelayQuery,
					queryVars: getAnchorPageRelayQueryVars(getPageIdKeywords(route.keyword), location),
					isRelayEnabled: true,
					relayApi: RELAY_API_CONTENTFUL,
				};
			}

			// features anchor is branded default + CB subanchor traffic
			// Also includes migrated anchors because they are wrapped in the same experiment, handled separately through uniform
			if (
				route.variant &&
				(route.variant === 'paid-lp-conf-features' ||
					isInConfluenceMigratedAnchors(route.variant) ||
					isInConfluenceCBSubAnchorCMSVariant(route.variant)) &&
				!userLoggedIn
			) {
				const confluenceContextualBanditsExperiment = getExperimentWithExposures(
					experiments.confluenceContextualBanditsInWae,
				);

				if (
					confluenceContextualBanditsExperiment.type === 'enrolled' &&
					confluenceContextualBanditsExperiment.value === true
				) {
					return {
						Component: UniformEntryPage,
						entryName: 'uniform-entry-page',
						// eslint-disable-next-line @typescript-eslint/no-explicit-any
						isUniformCanary: (route as any).isUniformCanary,
						isS3UniformRoute: true,
					};
				}
			}

			switch (route.variant) {
				case 'paid-lp-conf-features':
					return {
						Component: ConfluenceSoftwarePageVariantPaidLpConfFeatures,
						entryName: 'confluence-software-page-variant-paid-lp-conf-features',
					};
				case 'paid-lp-confl-template':
					return {
						Component: ConfluenceSoftwarePageVariantPaidLpConflTemplate,
						entryName: 'confluence-software-page-variant-paid-lp-confl-template',
						query: AnchorPageRelayQuery,
						queryVars: getAnchorPageRelayQueryVars(route.variant, location),
						isRelayEnabled: true,
						relayApi: RELAY_API_CONTENTFUL,
					};
				case 'paid-lp-confl-documentation':
					return {
						Component: ConfluenceSoftwarePageVariantPaidLpConflDocumentation,
						entryName: 'confluence-software-page-variant-paid-lp-confl-documentation',
						query: AnchorPageRelayQuery,
						queryVars: getAnchorPageRelayQueryVars(route.variant, location),
						isRelayEnabled: true,
						relayApi: RELAY_API_CONTENTFUL,
					};
				case 'anchor-lp-conf-collaboration':
					return {
						Component: ConfluenceSoftwarePageVariantAnchorLpConfCollaboration,
						entryName: 'confluence-software-page-variant-anchor-lp-conf-collaboration',
						query: AnchorPageRelayQuery,
						queryVars: getAnchorPageRelayQueryVars(route.variant, location),
						isRelayEnabled: true,
						relayApi: RELAY_API_CONTENTFUL,
					};
				case 'anchor-lp-conf-automation':
					return {
						Component: ConfluenceSoftwarePageAnchorAutomation,
						entryName: 'confluence-software-anchor-lp-automation',
						query: AnchorPageRelayQuery,
						queryVars: getAnchorPageRelayQueryVars(route.variant, location),
						isRelayEnabled: true,
						relayApi: RELAY_API_CONTENTFUL,
					};
				case 'anchor-lp-conf-teamwork':
					return {
						Component: ConfluenceSoftwarePageAnchorTeamwork,
						entryName: 'confluence-software-anchor-lp-teamwork',
						query: AnchorPageRelayQuery,
						queryVars: getAnchorPageRelayQueryVars(route.variant, location),
						isRelayEnabled: true,
						relayApi: RELAY_API_CONTENTFUL,
					};
				case 'anchor-lp-conf-competitors':
					return {
						Component: ConfluenceSoftwarePageAnchorCompetitors,
						entryName: 'confluence-software-anchor-lp-competitors',
						query: AnchorPageRelayQuery,
						queryVars: getAnchorPageRelayQueryVars(route.variant, location),
						isRelayEnabled: true,
						relayApi: RELAY_API_CONTENTFUL,
					};
				case 'anchor-lp-conf-work-management':
					return {
						Component: ConfluenceSoftwarePageAnchorWorkManagement,
						entryName: 'confluence-software-anchor-lp-work-management',
						query: AnchorPageRelayQuery,
						queryVars: getAnchorPageRelayQueryVars(route.variant, location),
						isRelayEnabled: true,
						relayApi: RELAY_API_CONTENTFUL,
					};
				case 'anchor-lp-conf-team-engineering':
					return {
						Component: ConfluenceSoftwarePageAnchorEngineering,
						entryName: 'confluence-software-anchor-lp-team-engineering',
						query: AnchorPageRelayQuery,
						queryVars: getAnchorPageRelayQueryVars(route.variant, location),
						isRelayEnabled: true,
						relayApi: RELAY_API_CONTENTFUL,
					};
				case 'anchor-lp-conf-team-marketing':
					return {
						Component: ConfluenceSoftwarePageAnchorMarketing,
						entryName: 'confluence-software-anchor-lp-team-marketing',
						query: AnchorPageRelayQuery,
						queryVars: getAnchorPageRelayQueryVars(route.variant, location),
						isRelayEnabled: true,
						relayApi: RELAY_API_CONTENTFUL,
					};
				case 'anchor-lp-conf-team-legal':
					return {
						Component: ConfluenceSoftwarePageAnchorLegal,
						entryName: 'confluence-software-anchor-lp-team-legal',
						query: AnchorPageRelayQuery,
						queryVars: getAnchorPageRelayQueryVars(route.variant, location),
						isRelayEnabled: true,
						relayApi: RELAY_API_CONTENTFUL,
					};
				case 'anchor-lp-conf-team-hr':
					return {
						Component: ConfluenceSoftwarePageAnchorHr,
						entryName: 'confluence-software-anchor-lp-team-hr',
						query: AnchorPageRelayQuery,
						queryVars: getAnchorPageRelayQueryVars(route.variant, location),
						isRelayEnabled: true,
						relayApi: RELAY_API_CONTENTFUL,
					};
				case 'anchor-lp-conf-team-operations':
					return {
						Component: ConfluenceSoftwarePageAnchorOperations,
						entryName: 'confluence-software-anchor-lp-team-operations',
						query: AnchorPageRelayQuery,
						queryVars: getAnchorPageRelayQueryVars(route.variant, location),
						isRelayEnabled: true,
						relayApi: RELAY_API_CONTENTFUL,
					};
				case 'anchor-lp-conf-team-design':
					return {
						Component: ConfluenceSoftwarePageAnchorDesign,
						entryName: 'confluence-software-anchor-lp-team-design',
						query: AnchorPageRelayQuery,
						queryVars: getAnchorPageRelayQueryVars(route.variant, location),
						isRelayEnabled: true,
						relayApi: RELAY_API_CONTENTFUL,
					};
				case 'anchor-lp-conf-team-programmgmt':
					return {
						Component: ConfluenceSoftwarePageAnchorProgramMgmt,
						entryName: 'confluence-software-anchor-lp-team-programmgmt',
						query: AnchorPageRelayQuery,
						queryVars: getAnchorPageRelayQueryVars(route.variant, location),
						isRelayEnabled: true,
						relayApi: RELAY_API_CONTENTFUL,
					};
				case 'anchor-lp-conf-team-customerservice':
					return {
						Component: ConfluenceSoftwarePageAnchorCustomerService,
						entryName: 'confluence-software-anchor-lp-team-customerservice',
						query: AnchorPageRelayQuery,
						queryVars: getAnchorPageRelayQueryVars(route.variant, location),
						isRelayEnabled: true,
						relayApi: RELAY_API_CONTENTFUL,
					};
				default:
					if (checkFeatureFlagGate(featureFlags.dxpMigrationConfluenceProductTourGate)) {
						return {
							Component: UniformEntryPage,
							entryName: 'uniform-entry-page',
							// eslint-disable-next-line @typescript-eslint/no-explicit-any
							isUniformCanary: (route as any).isUniformCanary,
							isS3UniformRoute: true,
						};
					} else {
						return {
							Component: ConfluenceSoftwarePage,
							entryName: 'confluence-software-page',
						};
					}
			}
		case 'software/jira/service-management/product-pricing':
			const jiraServiceManagementProductPricingTitle =
				getJiraServiceManagementPricingTitleInternal();

			return {
				Component: JiraServiceManagementPricingPage,
				entryName: 'jira-service-management-pricing-page',
				query: PricingPageRelayQuery,
				queryVars: getPricingPageRelayQueryVars(
					'jira-service-management',
					location,
					jiraServiceManagementProductPricingTitle,
				),
				isRelayEnabled: true,
				relayApi: RELAY_API_CONTENTFUL,
			};
		case 'software/jira/service-management/pricing':
			const jiraServiceManagementPricingTitle = getJiraServiceManagementPricingTitleInternal();

			return {
				Component: JiraServiceManagementPricingPage,
				entryName: 'jira-service-management-pricing-page',
				query: PricingPageRelayQuery,
				queryVars: getPricingPageRelayQueryVars(
					'jira-service-management',
					location,
					jiraServiceManagementPricingTitle,
				),
				isRelayEnabled: true,
				relayApi: RELAY_API_CONTENTFUL,
			};

		case 'software/confluence/pricing':
			const isWaeMigration = checkFeatureFlagGate(featureFlags.confluencePricingPageWaeMigration);
			const isConfluenceExperiment = getExperiment(experiments.confluencePricingPageWaeMigration);

			if (
				isWaeMigration &&
				isConfluenceExperiment.type === 'enrolled' &&
				isConfluenceExperiment.value === true
			) {
				return {
					Component: UniformConfluencePricingEntryPage,
					entryName: 'uniform-confluence-pricing-entry-page',
					isUniformCanary: route.isUniformCanary,
					isS3UniformRoute: true,
				};
			}
			const confluencePricingTitleInternal = getConfluencePricingTitleInternal();
			return {
				Component: ConfluencePricingPage,
				entryName: 'confluence-pricing-page',
				query: PricingPageRelayQuery,
				queryVars: getPricingPageRelayQueryVars(
					'confluence',
					location,
					confluencePricingTitleInternal,
				),
				isRelayEnabled: true,
				relayApi: RELAY_API_CONTENTFUL,
			};

		case 'software/confluence/free':
			return {
				Component: ConfluenceFreePage,
				entryName: 'confluence-free-page',
			};

		case 'software/confluence/templates': {
			return {
				Component: ConfluenceTemplatesContentfulRelayPage,
				entryName: 'confluence-templates-contentful-relay-page',
				query: ConfluenceTemplatesUnifiedContentfulRelayPageQuery,
				queryVars: getConfluenceTemplatesUnifiedContentfulRelayPageQueryVars(location),
				isRelayEnabled: true,
				relayApi: RELAY_API_CONTENTFUL,
			};
		}

		case 'software':
			return {
				Component: UniformEntryPage,
				entryName: 'uniform-entry-page',
				isUniformCanary: route.isUniformCanary,
				isS3UniformRoute: true,
			};

		case 'try':
			return {
				Component: TryPage,
				entryName: 'try-page',
			};

		case 'try/cloud/signup':
			return {
				Component: SignupPage,
				entryName: 'signup-page',
			};

		case 'try/cloud/loom/choose':
			return {
				Component: LoomChoicePage,
				entryName: 'loom-choice-page',
			};

		case 'ondemand/signup/checkemail':
			return {
				Component: CheckemailPage,
				entryName: 'checkemail-page',
			};

		case 'ondemand/signup/confirmation':
		case 'buy/cloud/confirmation':
			return {
				Component: ConfirmationPage,
				entryName: 'confirmation-page',
				featureFlags: route.featureFlags,
			};

		case 'software/confluence/templates/search': {
			return {
				Component: ConfluenceTemplateSearchContentfulRelayPage,
				entryName: 'confluence-template-search-contentful-relay-page',
				query: ConfluenceTemplatesUnifiedContentfulRelayPageQuery,
				queryVars: getConfluenceTemplatesUnifiedContentfulRelayPageQueryVars(location),
				isRelayEnabled: true,
				relayApi: RELAY_API_CONTENTFUL,
			};
		}

		case `software/confluence/templates/categories${getRouteExtension(
			route.path,
			'software/confluence/templates/categories',
		)}`: {
			return {
				Component: ConfluenceTemplateCategoryContentfulRelayPage,
				entryName: 'confluence-template-category-contentful-relay-page',
				query: ConfluenceTemplatesUnifiedContentfulRelayPageQuery,
				queryVars: getConfluenceTemplatesUnifiedContentfulRelayPageQueryVars(location),
				isRelayEnabled: true,
				relayApi: RELAY_API_CONTENTFUL,
			};
		}

		case `software/confluence/templates/collections${getRouteExtension(
			route.path,
			'software/confluence/templates/collections',
		)}`: {
			return {
				Component: ConfluenceTemplateCollectionDetailsContentfulRelayPage,
				entryName: 'confluence-template-collection-details-contentful-relay-page',
				query: ConfluenceTemplateCollectionDetailsContentfulRelayPageQuery,
				queryVars: getConfluenceTemplateCollectionDetailsContentfulRelayPageQueryVars(location),
				isRelayEnabled: true,
				relayApi: RELAY_API_CONTENTFUL,
			};
		}
		case `software/jira/templates/service-management`:
			return {
				Component: JiraTemplateCategoryPage,
				entryName: 'jira-template-category-page',
			};

		case 'enterprise/contact':
			return {
				Component: EnterpriseContactSalesPage,
				entryName: 'enterprise-contact-sales-page',
			};

		case 'enterprise/contact-thanks':
			switch (route.variant) {
				case 'product-features':
					return {
						Component: EnterpriseContactThanksPage,
						entryName: 'enterprise-contact-thanks-page',
					};
				case 'pricing-quotes':
					return {
						Component: EnterpriseContactThanksPage,
						entryName: 'enterprise-contact-thanks-page',
					};
				case 'migration-options':
					return {
						Component: EnterpriseContactThanksPage,
						entryName: 'enterprise-contact-thanks-page',
					};
				case 'billing-licensing':
					return {
						Component: EnterpriseContactThanksPage,
						entryName: 'enterprise-contact-thanks-page',
					};
				case 'technical-support':
					return {
						Component: EnterpriseContactThanksPage,
						entryName: 'enterprise-contact-thanks-page',
					};
				default:
					return {
						Component: EnterpriseContactThanksPage,
						entryName: 'enterprise-contact-thanks-page',
					};
			}

		case 'teams/nonprofits/discount-pricing':
			return {
				Component: NonProfitPricingPage,
				entryName: 'non-profit-pricing-page',
				query: PricingPageRelayQuery,
				queryVars: getPricingPageRelayQueryVars('nonprofit', location),
				isRelayEnabled: true,
				relayApi: RELAY_API_CONTENTFUL,
			};

		case `software/confluence/templates${getRouteExtension(
			route.path,
			'software/confluence/templates',
		)}`: {
			return {
				Component: UniformEntryPage,
				entryName: 'uniform-entry-page',
				isUniformCanary: route.isUniformCanary,
				isS3UniformRoute: true,
			};
		}
		case 'cdn-library':
			return {
				Component: CDNLibrary,
				entryName: 'cdn-library',
			};
		case 'search':
			return {
				Component: SearchResultsPage,
				entryName: 'search',
			};

		case '': {
			return {
				Component: UniformEntryPage,
				entryName: 'uniform-entry-page',
				isUniformCanary: route.isUniformCanary,
				isS3UniformRoute: true,
			};
		}
		default: {
			return { Component: FourOhFourPage, entryName: '404' };
		}
	}
};

const getRouteExtension = (path: string, baseTemplate: string) => {
	const pathExtn = path.split(baseTemplate).splice(1).join(baseTemplate);
	return pathExtn ? pathExtn : '';
};
